import React, { useState } from 'react';
import { X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = async () => {
  if (typeof chrome !== 'undefined' && chrome.storage) {
    const { accessToken } = await chrome.storage.local.get('accessToken');
    if (accessToken && accessToken !== 'session_active') {
      return { Authorization: `Bearer ${accessToken}` };
    }
  }
  return {};
};

export default function CreatePromptModal({ onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    function_tag: '',
    category_tag: '',
    model_tag: '',
    commentary: '',
    is_private: true
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const headers = await getAuthHeaders();
      await axios.post(`${API_URL}/api/prompts`, formData, {
        withCredentials: true,
        headers
      });
      toast.success('Prompt created successfully');
      onSuccess();
    } catch (error) {
      toast.error('Failed to create prompt');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="create-prompt-modal">
      <div className="bg-white rounded-xl p-6 w-[360px] max-h-[560px] overflow-y-auto shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl tracking-tight font-semibold text-[#1C1917]">Create Prompt</h2>
          <button onClick={onClose} className="text-[#78716C] hover:text-[#1C1917]" data-testid="close-modal-button">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="text-xs tracking-wider uppercase text-[#78716C] mb-1 block">Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="rounded-lg border border-[#E7E5E4] bg-white px-3 py-2 text-sm text-[#1C1917] placeholder-[#78716C] focus:border-[#2C4C3B] focus:ring-1 focus:ring-[#2C4C3B] focus:outline-none w-full"
              placeholder="e.g., Weekly Sales Report"
              required
              data-testid="title-input"
            />
          </div>

          <div>
            <label className="text-xs tracking-wider uppercase text-[#78716C] mb-1 block">Content *</label>
            <textarea
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              className="rounded-lg border border-[#E7E5E4] bg-white px-3 py-2 text-sm text-[#1C1917] placeholder-[#78716C] focus:border-[#2C4C3B] focus:ring-1 focus:ring-[#2C4C3B] focus:outline-none w-full"
              rows="4"
              placeholder="Enter your prompt here..."
              required
              data-testid="content-input"
            />
          </div>

          <div>
            <label className="text-xs tracking-wider uppercase text-[#78716C] mb-1 block">Function Tag</label>
            <select
              value={formData.function_tag}
              onChange={(e) => setFormData({ ...formData, function_tag: e.target.value })}
              className="rounded-lg border border-[#E7E5E4] bg-white px-3 py-2 text-sm text-[#1C1917] focus:border-[#2C4C3B] focus:ring-1 focus:ring-[#2C4C3B] focus:outline-none w-full"
              data-testid="function-tag-select"
            >
              <option value="">Select function...</option>
              <option value="Sales">Sales</option>
              <option value="Marketing">Marketing</option>
              <option value="Customer Success">Customer Success</option>
              <option value="Engineering">Engineering</option>
              <option value="Product">Product</option>
            </select>
          </div>

          <div>
            <label className="text-xs tracking-wider uppercase text-[#78716C] mb-1 block">Category Tag</label>
            <select
              value={formData.category_tag}
              onChange={(e) => setFormData({ ...formData, category_tag: e.target.value })}
              className="rounded-lg border border-[#E7E5E4] bg-white px-3 py-2 text-sm text-[#1C1917] focus:border-[#2C4C3B] focus:ring-1 focus:ring-[#2C4C3B] focus:outline-none w-full"
              data-testid="category-tag-select"
            >
              <option value="">Select category...</option>
              <option value="Ad hoc">Ad hoc</option>
              <option value="Weekly recurring">Weekly recurring</option>
              <option value="Monthly recurring">Monthly recurring</option>
              <option value="Daily recurring">Daily recurring</option>
            </select>
          </div>

          <div>
            <label className="text-xs tracking-wider uppercase text-[#78716C] mb-1 block">Model Tag</label>
            <select
              value={formData.model_tag}
              onChange={(e) => setFormData({ ...formData, model_tag: e.target.value })}
              className="rounded-lg border border-[#E7E5E4] bg-white px-3 py-2 text-sm text-[#1C1917] focus:border-[#2C4C3B] focus:ring-1 focus:ring-[#2C4C3B] focus:outline-none w-full"
              data-testid="model-tag-select"
            >
              <option value="">Select model...</option>
              <option value="ChatGPT">ChatGPT</option>
              <option value="Claude">Claude</option>
              <option value="Gemini">Gemini</option>
            </select>
          </div>

          <div>
            <label className="text-xs tracking-wider uppercase text-[#78716C] mb-1 block">Commentary</label>
            <textarea
              value={formData.commentary}
              onChange={(e) => setFormData({ ...formData, commentary: e.target.value })}
              className="rounded-lg border border-[#E7E5E4] bg-white px-3 py-2 text-sm text-[#1C1917] placeholder-[#78716C] focus:border-[#2C4C3B] focus:ring-1 focus:ring-[#2C4C3B] focus:outline-none w-full"
              rows="2"
              placeholder="Add notes about intended use case..."
              data-testid="commentary-input"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_private"
              checked={formData.is_private}
              onChange={(e) => setFormData({ ...formData, is_private: e.target.checked })}
              className="rounded border-[#E7E5E4] text-[#2C4C3B] focus:ring-[#2C4C3B]"
              data-testid="private-checkbox"
            />
            <label htmlFor="is_private" className="text-sm text-[#57534E]">Keep private</label>
          </div>

          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-medium transition-colors border border-[#E7E5E4] bg-white text-[#1C1917] hover:bg-[#FAFAF9] flex-1"
              data-testid="cancel-button"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-medium transition-colors focus:ring-2 focus:ring-[#E7E5E4] focus:outline-none bg-[#2C4C3B] text-white hover:bg-[#243D30] flex-1 disabled:opacity-50"
              data-testid="create-button"
            >
              {loading ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}